import { useEffect, useRef, useCallback } from "react";
import { useEvents } from "@/context/EventsContext";
import { LoadingView } from "@/components";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EventGroupCard } from "./components/EventGroupCard";

export const EventsPage = (): React.ReactNode => {
  const { events, groups, loading, error, hasMore, loadMore, refresh } = useEvents();
  const observerRef = useRef<HTMLDivElement>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasMore && !loading) {
        loadMore();
      }
    },
    [hasMore, loading, loadMore]
  );

  useEffect(() => {
    const el = observerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleObserver]);

  if (loading && events.length === 0) return <LoadingView />;

  return (
    <div className="grid gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Events</h1>
          <p className="text-muted-foreground mt-2">Order activity feed</p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {error && <p className="text-red-500">Error: {error}</p>}

      {events.length === 0 && !loading && (
        <p className="text-muted-foreground">
          No events yet. Events will appear here when orders are opened or closed.
        </p>
      )}

      <div className="flex flex-col gap-2">
        {groups.map((group) => (
          <EventGroupCard key={group.key} group={group} />
        ))}
      </div>

      <div ref={observerRef} className="h-4" />

      {loading && events.length > 0 && (
        <div className="flex justify-center py-4">
          <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {!hasMore && events.length > 0 && (
        <p className="text-center text-xs text-muted-foreground py-4">No more events</p>
      )}
    </div>
  );
};
